import React, { useMemo } from 'react';
import type { GalleryAsset } from './GenericPreviewPane';
import { AssetContainer } from './GenericPreviewPane';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { SUITE_IMAGE_KEYS, suiteImageKey, getSuiteImageUrl } from '@apps/card/utils/suiteImage';

// Re-export so existing consumers that import from here continue to work.
export { hasSuiteImage } from '@apps/card/utils/suiteImage';

/**
 * The suite image asset for a design + colorway, or `null` when the flag is off
 * or no image exists for it.
 *
 * Kept separate from `useCardPreviewPaneAssets` so the caller can place it
 * directly after the card front. Returning it at the head of the generic extra
 * assets wasn't enough: those are appended after the in-situ and video entries,
 * which pushed the suite to third or fourth for designs that have them.
 */
export const useSuiteImageAsset = (themeId?: string, colorwayId?: string): GalleryAsset | null => {
  const { tq } = useTranslation('stationery');
  const { value: suiteImageFlag } = useFeatureValue('printPdpSuiteImage');
  const isSuiteImageEnabled = suiteImageFlag === 'on';

  return useMemo(() => {
    if (!isSuiteImageEnabled || !themeId) return null;

    const key = suiteImageKey(themeId, colorwayId);
    if (!SUITE_IMAGE_KEYS.has(key)) return null;

    const suiteUrl = getSuiteImageUrl(key);
    return {
      render: () => (
        <AssetContainer
          enforceAspectRatio
          key="suiteImage"
          label={tq`The Suite`}
          backgroundImageUrl={suiteUrl}
        />
      ),
      thumbnailUrl: suiteUrl,
    };
  }, [tq, themeId, colorwayId, isSuiteImageEnabled]);
};
